import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import makeAnimated from "react-select/animated";
import Header from '../../layouts/Header';
import axios from '../../api/axios';
import { Button, Card, Table, Badge, Spinner, Modal, Form, InputGroup, Pagination } from 'react-bootstrap';
import { ArrowLeft, InfoCircle, Tag, Shop, Clock, CashCoin, Gift, Percent, ArrowUp, ArrowDown, Search, Filter } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';

const ProductDetailReport = () => {
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const navigate = useNavigate();

    const accessToken = localStorage.getItem("authToken");
    const [productsList, setProductsList] = useState([]);
    const [productDetailData, setProductDetailData] = useState({});
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [loading, setLoading] = useState(false);
    const [reportLoading, setReportLoading] = useState(false);
    
    // Coupons pagination and filtering
    const [showAllCoupons, setShowAllCoupons] = useState(false);
    const [couponSearch, setCouponSearch] = useState('');
    const [couponFilter, setCouponFilter] = useState('ALL');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 12;

    const animatedComponents = makeAnimated();

    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            width: "100%",
            borderRadius: "6px",
            minHeight: "45px",
            borderColor: state.isFocused ? "#80bdff" : "#ced4da",
            boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : null,
            "&:hover": {
                borderColor: state.isFocused ? "#80bdff" : "#ced4da",
            },
            backgroundColor: skin === "dark" ? "#2b2b2b" : "white",
            color: skin === "dark" ? "white" : "black",
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: skin === "dark" ? "#2b2b2b" : "white",
            color: skin === "dark" ? "white" : "black",
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused 
                ? (skin === "dark" ? "#404040" : "#e9ecef") 
                : (skin === "dark" ? "#2b2b2b" : "white"),
            color: skin === "dark" ? "white" : "black",
            "&:hover": {
                backgroundColor: skin === "dark" ? "#404040" : "#e9ecef",
            },
        }),
        singleValue: (provided) => ({
            ...provided,
            color: skin === "dark" ? "white" : "black",
        }),
        placeholder: (provided) => ({
            ...provided,
            color: "#6c757d",
        }),
        input: (provided) => ({
            ...provided,
            color: skin === "dark" ? "white" : "black",
        }),
    };

    const getProductsList = async () => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("product", { headers });
            if (response?.data?.status === true) {
                const products = response?.data?.data?.products || [];
                const formattedProducts = products.map(product => ({
                    value: product._id,
                    label: product.name,
                    data: product
                }));
                setProductsList(formattedProducts);
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    const getProductDetailReport = async (productId) => {
        if (!productId) return;
        
        setReportLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get(`product/getProductDetails?productId=${productId}`, { headers });
            if (response?.data?.status === true) {
                setProductDetailData(response?.data?.data || {});
                // Reset coupon states when new product is selected
                setShowAllCoupons(false);
                setCouponSearch('');
                setCouponFilter('ALL');
                setCurrentPage(1);
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setReportLoading(false);
        }
    };

    const handleProductSelect = (selectedOption) => {
        setSelectedProduct(selectedOption);
        if (selectedOption) {
            getProductDetailReport(selectedOption.value);
        } else {
            setProductDetailData({});
        }
    };

    const handleApiError = (error) => {
        let errorMessage = 'An error occurred';
        if (error.response?.status === 406) {
            errorMessage = error.response?.data?.error?.message || error.response?.data?.error;
        } else if (error.response?.status === 412) {
            errorMessage = error.response?.data?.data?.message;
        } else if (error.response?.status === 500) {
            errorMessage = error.response?.data?.error;
        }

        toast.error(errorMessage, {
            position: "top-right",
            autoClose: 3000,
            theme: "light",
        });
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(price);
    };

    const getProductStatusBadge = (status) => {
        const statusConfig = {
            "ACTIVE": { variant: "success", text: "Active" },
            "INACTIVE": { variant: "secondary", text: "Inactive" },
            "DRAFT": { variant: "warning", text: "Draft" }
        };
        
        const config = statusConfig[status] || { variant: "secondary", text: status };
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const getFoodTypeBadge = (type) => {
        const typeConfig = {
            "VEG": { variant: "success", text: "Veg" },
            "NON_VEG": { variant: "danger", text: "Non-Veg" },
            "EGG": { variant: "warning", text: "Egg" }
        };
        
        const config = typeConfig[type] || { variant: "secondary", text: type };
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const getOutletStatusBadge = (status) => {
        const statusConfig = {
            "IN_STOCK": { variant: "success", text: "In Stock" },
            "OUT_OF_STOCK": { variant: "danger", text: "Out of Stock" },
            "EXHAUSTED": { variant: "warning", text: "Exhausted" },
            "NOT_AVAILABLE": { variant: "secondary", text: "Not Available" }
        };
        
        const config = statusConfig[status] || { variant: "secondary", text: status };
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const getCouponTypeIcon = (coupon) => {
        if (coupon.isBogoCoupon) return <Gift className="me-1" />;
        if (coupon.discountPercent) return <Percent className="me-1" />;
        if (coupon.discountAmount) return <CashCoin className="me-1" />;
        if (coupon.discountPrice) return <Tag className="me-1" />;
        if (coupon.newSizeUpgradeAddon) return <ArrowUp className="me-1" />;
        if (coupon.isFreebieCoupon) return <Gift className="me-1" />;
        return <InfoCircle className="me-1" />;
    };

    const getCouponTypeText = (coupon) => {
        if (coupon.isBogoCoupon) return "BOGO";
        if (coupon.discountPercent) return `Discount (${coupon.discountPercent}%)`;
        if (coupon.discountAmount) return `Fixed Discount (₹${coupon.discountAmount})`;
        if (coupon.discountPrice) return `Fixed Price (₹${coupon.discountPrice})`;
        if (coupon.newSizeUpgradeAddon) return "Size Upgrade";
        if (coupon.isFreebieCoupon) return `Freebie (${coupon.freebieQuantity}x)`;
        return "Special Offer";
    };

    const getCouponTypeForFilter = (coupon) => {
        if (coupon.isBogoCoupon) return 'BOGO';
        if (coupon.discountPercent) return 'PERCENTAGE';
        if (coupon.discountAmount) return 'FIXED_AMOUNT';
        if (coupon.discountPrice) return 'FIXED_PRICE';
        if (coupon.newSizeUpgradeAddon) return 'SIZE_UPGRADE';
        if (coupon.isFreebieCoupon) return 'FREEBIE';
        return 'OTHER';
    };

    const getCouponTypeBadge = (coupon) => {
        const type = getCouponTypeForFilter(coupon);
        const badgeConfig = {
            'BOGO': { variant: 'primary', text: 'BOGO' },
            'PERCENTAGE': { variant: 'success', text: 'Percentage' },
            'FIXED_AMOUNT': { variant: 'info', text: 'Fixed Amount' },
            'FIXED_PRICE': { variant: 'warning', text: 'Fixed Price' },
            'SIZE_UPGRADE': { variant: 'secondary', text: 'Size Upgrade' },
            'FREEBIE': { variant: 'danger', text: 'Freebie' },
            'OTHER': { variant: 'dark', text: 'Other' }
        };
        const config = badgeConfig[type] || badgeConfig.OTHER;
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const handleViewAllCoupons = () => {
        setShowAllCoupons(true);
        
    }

    // Filter and search coupons
    const filteredCoupons = ([]).filter(coupon => {
        // Search filter
        const searchMatch = couponSearch === '' || 
            coupon.title?.toLowerCase().includes(couponSearch.toLowerCase()) ||
            coupon.couponCode?.toLowerCase().includes(couponSearch.toLowerCase()) ||
            coupon.description?.toLowerCase().includes(couponSearch.toLowerCase());
        
        // // Type filter
        const typeMatch = couponFilter === 'ALL' || 
            getCouponTypeForFilter(coupon) === couponFilter ||
            (couponFilter === 'ACTIVE' && coupon.status === 'ACTIVE') ||
            (couponFilter === 'INACTIVE' && coupon.status !== 'ACTIVE');
        
        return searchMatch && typeMatch;
    });

    // Pagination calculations
    const totalPages = Math.ceil(filteredCoupons.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentCoupons = filteredCoupons.slice(startIndex, endIndex);

    // Handle page change
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Generate pagination items
    const paginationItems = [];
    for (let number = 1; number <= totalPages; number++) {
        paginationItems.push(
            <Pagination.Item
                key={number}
                active={number === currentPage}
                onClick={() => handlePageChange(number)}
            >
                {number}
            </Pagination.Item>
        );
    }

    const coupons = productDetailData?.applicableCoupons || [];

    useEffect(() => {
        getProductsList();
    }, []);

    const product = productDetailData?.product || {};
    const outletAvailability = productDetailData?.outletAvailability || [];
    const menuGroupPricing = productDetailData?.menuGroupPricing || [];

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "bg-dark text-light" : "bg-light"}`}>
                <div className="card shadow-sm border-0">
                    <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                        <div>
                            <h4 className="mb-0 fw-bold d-inline-block">
                                <i className="bi bi-box-seam me-2"></i>PRODUCT DETAIL REPORT
                            </h4>
                        </div>
                        <div>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => navigate('/reports/all-reports')}
                                className="me-3 d-flex align-items-center fw-semibold"
                                style={{ height: '38px' }}
                            >
                                <ArrowLeft className="me-1" /> Back to Reports
                            </Button>
                        </div>
                    </div>
                    <hr className='mt-0 mb-0' />
                    
                    <div className="card-body">
                        {/* Product Selection */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <label className="form-label fw-semibold mb-2">
                                    <i className="bi bi-search me-2"></i>Select Product
                                </label>
                                <Select
                                    components={animatedComponents}
                                    options={productsList}
                                    styles={customStyles}
                                    placeholder="Search and select a product..."
                                    value={selectedProduct}
                                    onChange={handleProductSelect}
                                    isClearable
                                    isLoading={loading}
                                />
                                <div className="form-text mt-1">
                                    Select a product to view detailed information, pricing, coupons, and outlet availability.
                                </div>
                            </div>
                        </div>

                        {reportLoading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2">Loading product details...</p>
                            </div>
                        ) : selectedProduct ? (
                            <>
                                {/* Product Information Card */}
                                <div className="row mb-4">
                                    <div className="col-12">
                                        <Card className="shadow-sm border-0">
                                            <Card.Body>
                                                <div className="d-flex align-items-start">
                                                    {product.displayImage && (
                                                        <div className="me-4">
                                                            <img 
                                                                src={`${process.env.REACT_APP_API_BASE_URL}/uploads/${product.displayImage}`}
                                                                alt={product.name}
                                                                className="rounded"
                                                                style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                                                            />
                                                        </div>
                                                    )}
                                                    <div className="flex-grow-1">
                                                        <div className="d-flex justify-content-between align-items-start">
                                                            <div>
                                                                <h4 className="fw-bold mb-1">{product.name}</h4>
                                                                <p className="text-muted mb-2">{product.description}</p>
                                                                <div className="d-flex gap-2 mb-2">
                                                                    {getProductStatusBadge(product.status)}
                                                                    {getFoodTypeBadge(product.foodType)}
                                                                    <Badge bg="info">{product.classType}</Badge>
                                                                    {product.isNewProduct && <Badge bg="primary">New</Badge>}
                                                                    {product.isEventProduct && <Badge bg="warning">Event</Badge>}
                                                                </div>
                                                            </div>
                                                            <div className="text-end">
                                                                <h3 className="fw-bold text-success mb-1">
                                                                    {formatPrice(product.offerPrice || 0)}
                                                                </h3>
                                                                <div className="text-decoration-line-through text-muted">
                                                                    MRP: {formatPrice(product.mrp || 0)}
                                                                </div>
                                                                <div className="text-danger fw-semibold">
                                                                    Save: {formatPrice((product.mrp || 0) - (product.offerPrice || 0))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="row mt-3">
                                                            <div className="col-md-3">
                                                                <div className="mb-2">
                                                                    <span className="fw-semibold me-2">Category:</span>
                                                                    <Badge bg="light" text="dark">
                                                                        {product.category?.name || 'N/A'}
                                                                    </Badge>
                                                                </div>
                                                                <div className="mb-2">
                                                                    <span className="fw-semibold me-2">Order Type:</span>
                                                                    <Badge bg={product.orderType === 'ALL' ? 'secondary' : 'info'}>
                                                                        {product.orderType}
                                                                    </Badge>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-3">
                                                                <div className="mb-2">
                                                                    <span className="fw-semibold me-2">Making Time:</span>
                                                                    <span><Clock className="me-1" />{product.makingTime || '0'} mins</span>
                                                                </div>
                                                                <div className="mb-2">
                                                                    <span className="fw-semibold me-2">Calories:</span>
                                                                    <span>{product.calories || '0'} kcal</span>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-3">
                                                                <div className="mb-2">
                                                                    <span className="fw-semibold me-2">Weight:</span>
                                                                    <span>{product.weight || '0'} {product.weightUnit}</span>
                                                                </div>
                                                                <div className="mb-2">
                                                                    <span className="fw-semibold me-2">Tax:</span>
                                                                    <span>{product.taxPercent || '0'}%</span>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-3">
                                                                <div className="mb-2">
                                                                    <span className="fw-semibold me-2">Sequence:</span>
                                                                    <span>{product.sequenceNumber || '0'}</span>
                                                                </div>
                                                                {product.hasAddon && (
                                                                    <div className="mb-2">
                                                                        <Badge bg="success">Has Addons</Badge>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {product.productsNature && product.productsNature.length > 0 && (
                                                            <div className="mt-3">
                                                                <span className="fw-semibold me-2">Product Nature:</span>
                                                                {product.productsNature.map((nature, index) => (
                                                                    <Badge key={index} bg="light" text="dark" className="me-2">
                                                                        {nature}
                                                                    </Badge>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                </div>

                                {/* Pricing by Menu Groups */}
                                {menuGroupPricing.length > 0 && (
                                    <div className="row mb-4">
                                        <div className="col-12">
                                            <Card className="shadow-sm border-0">
                                                <Card.Header className="bg-light border-0 py-3">
                                                    <h5 className="mb-0 fw-bold">
                                                        <i className="bi bi-currency-dollar me-2"></i>Pricing by Menu Groups
                                                    </h5>
                                                </Card.Header>
                                                <Card.Body>
                                                    <Table responsive hover className="mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th>Menu Group</th>
                                                                <th className="text-end">MRP Price</th>
                                                                <th className="text-end">Offer Price</th>
                                                                <th className="text-end">Discount</th>
                                                                <th className="text-end">Discount %</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {menuGroupPricing.map((group, index) => {
                                                                const discount = group.mrpPrice - group.offerPrice;
                                                                const discountPercent = group.mrpPrice > 0 ? Math.round((discount / group.mrpPrice) * 100) : 0;
                                                                return (
                                                                    <tr key={index}>
                                                                        <td>
                                                                            <div className="d-flex align-items-center">
                                                                                <span className="fw-semibold">{group.menuGroup?.name}</span>
                                                                            </div>
                                                                        </td>
                                                                        <td className="text-end">
                                                                            <span className="text-decoration-line-through text-muted">
                                                                                {formatPrice(group.mrpPrice)}
                                                                            </span>
                                                                        </td>
                                                                        <td className="text-end">
                                                                            <span className="fw-bold text-success">
                                                                                {formatPrice(group.offerPrice)}
                                                                            </span>
                                                                        </td>
                                                                        <td className="text-end">
                                                                            <span className="fw-semibold text-danger">
                                                                                -{formatPrice(discount)}
                                                                            </span>
                                                                        </td>
                                                                        <td className="text-end">
                                                                            <Badge bg={discountPercent > 0 ? "success" : "secondary"}>
                                                                                {discountPercent}%
                                                                            </Badge>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </Table>
                                                </Card.Body>
                                            </Card>
                                        </div>
                                    </div>
                                )}

                                {/* Applicable Coupons - Modal for All Coupons */}
                                {coupons.length > 0 && (
                                    <div className="row mb-4">
                                        <div className="col-12">
                                            <Card className="shadow-sm border-0">
                                                <Card.Header className="bg-light border-0 py-3 d-flex justify-content-between align-items-center">
                                                    <h5 className="mb-0 fw-bold">
                                                        <i className="bi bi-tag me-2"></i>Applicable Coupons
                                                        <Badge bg="primary" className="ms-2">{coupons.length}</Badge>
                                                    </h5>
                                                    {coupons.length > 12 && (
                                                        <Button 
                                                            variant="outline-primary" 
                                                            size="sm"
                                                            onClick={handleViewAllCoupons}
                                                        >
                                                            View All {coupons.length} Coupons
                                                        </Button>
                                                    )}
                                                </Card.Header>
                                                <Card.Body>
                                                    <div className="row">
                                                        {coupons.slice(0, 12).map((coupon, index) => (
                                                            <div key={index} className="col-md-6 col-lg-4 mb-3">
                                                                <Card className="h-100 border">
                                                                    <Card.Body>
                                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                                            <div>
                                                                                <h6 className="fw-bold mb-1">{coupon.title}</h6>
                                                                                <div className="d-flex align-items-center mb-1">
                                                                                    {getCouponTypeIcon(coupon)}
                                                                                    <small className="text-muted">{getCouponTypeText(coupon)}</small>
                                                                                </div>
                                                                            </div>
                                                                            <Badge bg={coupon.status === "ACTIVE" ? "success" : "secondary"}>
                                                                                {coupon.status}
                                                                            </Badge>
                                                                        </div>
                                                                        
                                                                        <p className="small text-muted mb-2">{coupon.description}</p>
                                                                        
                                                                        <div className="mb-2">
                                                                            <span className="fw-semibold">Code: </span>
                                                                            <Badge bg="dark" className="ms-1">{coupon.couponCode}</Badge>
                                                                        </div>
                                                                        
                                                                        <div className="d-flex flex-wrap gap-2 mb-2">
                                                                            {coupon.discountPercent && (
                                                                                <Badge bg="info">
                                                                                    Discount: {coupon.discountPercent}%
                                                                                </Badge>
                                                                            )}
                                                                            {coupon.discountAmount && (
                                                                                <Badge bg="info">
                                                                                    Discount: ₹{coupon.discountAmount}
                                                                                </Badge>
                                                                            )}
                                                                            {coupon.maxDiscountAmount && (
                                                                                <Badge bg="warning">
                                                                                    Max: ₹{coupon.maxDiscountAmount}
                                                                                </Badge>
                                                                            )}
                                                                            {coupon.minOrderAmount && (
                                                                                <Badge bg="secondary">
                                                                                    Min: ₹{coupon.minOrderAmount}
                                                                                </Badge>
                                                                            )}
                                                                        </div>
                                                                        
                                                                        <div className="mt-2">
                                                                            <span className="fw-semibold me-2">Usage:</span>
                                                                            <Badge bg={coupon.usageType === "MULTIPLE" ? "success" : "primary"}>
                                                                                {coupon.usageType}
                                                                            </Badge>
                                                                        </div>
                                                                    </Card.Body>
                                                                </Card>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </div>
                                    </div>
                                )}

                                {/* Modal for All Coupons */}
                                <Modal 
                                    show={showAllCoupons} 
                                    onHide={() => setShowAllCoupons(false)}
                                    size="xl"
                                    fullscreen="lg-down"
                                >
                                    <Modal.Header closeButton className="bg-light">
                                        <Modal.Title>
                                            <i className="bi bi-tag me-2"></i>
                                            All Coupons for {product.name}
                                            <Badge bg="primary" className="ms-2">{coupons.length}</Badge>
                                        </Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        {/* Search and Filter Controls */}
                                        <div className="row mb-3">
                                            <div className="col-md-8">
                                                <InputGroup>
                                                    <InputGroup.Text>
                                                        <Search />
                                                    </InputGroup.Text>
                                                    <Form.Control
                                                        placeholder="Search coupons by title, code, or description..."
                                                        value={couponSearch}
                                                        onChange={(e) => {
                                                            setCouponSearch(e.target.value);
                                                            setCurrentPage(1);
                                                        }}
                                                    />
                                                </InputGroup>
                                            </div>
                                            <div className="col-md-4">
                                                <InputGroup>
                                                    <InputGroup.Text>
                                                        <Filter />
                                                    </InputGroup.Text>
                                                    <Form.Select
                                                        value={couponFilter}
                                                        onChange={(e) => {
                                                            setCouponFilter(e.target.value);
                                                            setCurrentPage(1);
                                                        }}
                                                    >
                                                        <option value="ALL">All Types</option>
                                                        <option value="BOGO">BOGO Coupons</option>
                                                        <option value="PERCENTAGE">Percentage Discount</option>
                                                        <option value="FIXED_AMOUNT">Fixed Amount</option>
                                                        <option value="FIXED_PRICE">Fixed Price</option>
                                                        <option value="SIZE_UPGRADE">Size Upgrade</option>
                                                        <option value="FREEBIE">Freebie</option>
                                                        <option value="ACTIVE">Active Only</option>
                                                        <option value="INACTIVE">Inactive Only</option>
                                                    </Form.Select>
                                                </InputGroup>
                                            </div>
                                        </div>

                                        {/* Results Summary */}
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <div>
                                                Showing {currentCoupons.length} of {filteredCoupons.length} coupons
                                                {couponSearch && (
                                                    <span className="text-muted ms-2">
                                                        for "{couponSearch}"
                                                    </span>
                                                )}
                                            </div>
                                            <div>
                                                <Badge bg="info" className="me-2">
                                                    Page {currentPage} of {totalPages}
                                                </Badge>
                                            </div>
                                        </div>

                                        {/* Coupons Grid */}
                                        {filteredCoupons.length === 0 ? (
                                            <div className="text-center py-5">
                                                <i className="bi bi-search display-1 text-muted mb-3"></i>
                                                <h5>No coupons found</h5>
                                                <p className="text-muted">
                                                    Try adjusting your search or filter criteria
                                                </p>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="row">
                                                    {currentCoupons.map((coupon, index) => (
                                                        <div key={index} className="col-md-6 col-lg-4 mb-3">
                                                            <Card className="h-100 border">
                                                                <Card.Body>
                                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                                        <div>
                                                                            <h6 className="fw-bold mb-1">{coupon.title}</h6>
                                                                            <div className="d-flex align-items-center mb-1">
                                                                                {getCouponTypeIcon(coupon)}
                                                                                <small className="text-muted">{getCouponTypeText(coupon)}</small>
                                                                            </div>
                                                                        </div>
                                                                        <div className="text-end">
                                                                            <Badge bg={coupon.status === "ACTIVE" ? "success" : "secondary"}>
                                                                                {coupon.status}
                                                                            </Badge>
                                                                            <div className="mt-1">
                                                                                {getCouponTypeBadge(coupon)}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    
                                                                    <p className="small text-muted mb-2">{coupon.description}</p>
                                                                    
                                                                    <div className="mb-2">
                                                                        <span className="fw-semibold">Code: </span>
                                                                        <Badge bg="dark" className="ms-1">{coupon.couponCode}</Badge>
                                                                    </div>
                                                                    
                                                                    <div className="d-flex flex-wrap gap-1 mb-2">
                                                                        {coupon.discountPercent && (
                                                                            <Badge bg="info" className="small">
                                                                                {coupon.discountPercent}% Off
                                                                            </Badge>
                                                                        )}
                                                                        {coupon.discountAmount && (
                                                                            <Badge bg="info" className="small">
                                                                                ₹{coupon.discountAmount} Off
                                                                            </Badge>
                                                                        )}
                                                                        {coupon.maxDiscountAmount && (
                                                                            <Badge bg="warning" className="small">
                                                                                Max ₹{coupon.maxDiscountAmount}
                                                                            </Badge>
                                                                        )}
                                                                        {coupon.minOrderAmount && (
                                                                            <Badge bg="secondary" className="small">
                                                                                Min ₹{coupon.minOrderAmount}
                                                                            </Badge>
                                                                        )}
                                                                        {coupon.isMrpDiscount && (
                                                                            <Badge bg="danger" className="small">
                                                                                MRP Discount
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                    
                                                                    <div className="small text-muted mb-2">
                                                                        <div>
                                                                            <span className="fw-semibold">Usage: </span>
                                                                            <Badge bg={coupon.usageType === "MULTIPLE" ? "success" : "primary"} className="small">
                                                                                {coupon.usageType}
                                                                            </Badge>
                                                                        </div>
                                                                        {coupon.validityStartDate && (
                                                                            <div>
                                                                                <span className="fw-semibold">Valid: </span>
                                                                                {new Date(coupon.validityStartDate).toLocaleDateString()} - 
                                                                                {coupon.validityEndDate ? ` ${new Date(coupon.validityEndDate).toLocaleDateString()}` : ' No End Date'}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    
                                                                    {coupon.termsAndConditions && coupon.termsAndConditions.length > 0 && (
                                                                        <div className="small">
                                                                            <span className="fw-semibold">Terms: </span>
                                                                            <ul className="mb-0 ps-3">
                                                                                {coupon.termsAndConditions.map((term, idx) => (
                                                                                    <li key={idx} className="small text-muted">
                                                                                        {term}
                                                                                    </li>
                                                                                ))}
                                                                            </ul>
                                                                        </div>
                                                                    )}
                                                                </Card.Body>
                                                            </Card>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Pagination */}
                                                {totalPages > 1 && (
                                                    <div className="d-flex justify-content-center mt-4">
                                                        <Pagination>
                                                            <Pagination.Prev 
                                                                onClick={() => handlePageChange(currentPage - 1)}
                                                                disabled={currentPage === 1}
                                                            />
                                                            {paginationItems}
                                                            <Pagination.Next 
                                                                onClick={() => handlePageChange(currentPage + 1)}
                                                                disabled={currentPage === totalPages}
                                                            />
                                                        </Pagination>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button variant="secondary" onClick={() => setShowAllCoupons(false)}>
                                            Close
                                        </Button>
                                    </Modal.Footer>
                                </Modal>

                                {/* Outlet Availability */}
                                {outletAvailability.length > 0 && (
                                    <div className="row">
                                        <div className="col-12">
                                            <Card className="shadow-sm border-0">
                                                <Card.Header className="bg-light border-0 py-3">
                                                    <h5 className="mb-0 fw-bold">
                                                        <i className="bi bi-shop me-2"></i>Outlet Availability
                                                        <Badge bg="primary" className="ms-2">{outletAvailability.length}</Badge>
                                                    </h5>
                                                </Card.Header>
                                                <Card.Body>
                                                    <Table responsive hover className="mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th>Outlet Name</th>
                                                                <th>Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {outletAvailability.map((outlet, index) => (
                                                                <tr key={index}>
                                                                    <td>
                                                                        <div className="d-flex align-items-center">
                                                                            <Shop className="me-2 text-muted" />
                                                                            <span className="fw-semibold">{outlet.outletName}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td>{getOutletStatusBadge(outlet.status)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </Card.Body>
                                            </Card>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-5">
                                <div className="mb-3">
                                    <i className="bi bi-box-seam display-1 text-muted"></i>
                                </div>
                                <h5 className="fw-semibold mb-2">No Product Selected</h5>
                                <p className="text-muted mb-4">
                                    Please select a product from the dropdown above to view detailed information.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </React.Fragment>
    )
}

export default ProductDetailReport